import { useEffect, useState } from 'react';
import { getTableLeaderboard, type LeaderboardEntry } from './api';
import styles from './TableLeaderboard.module.css';

interface TableLeaderboardProps {
  tableId: string;
  defaultOpen?: boolean;
  refreshKey?: number;
  limit?: number;
  showToggle?: boolean;
}

export default function TableLeaderboard({
  tableId,
  defaultOpen = false,
  refreshKey = 0,
  limit = 10,
  showToggle = true,
}: TableLeaderboardProps) {
  const [open, setOpen] = useState(defaultOpen || !showToggle);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState<number | null>(null);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');

    void getTableLeaderboard(tableId, limit, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setVersion(result.versionNumber);
        setEntries(result.entries);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof Error ? caught.message : 'Leaderboard unavailable.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [limit, open, refreshKey, tableId]);

  return (
    <div className={styles.block}>
      {showToggle && (
        <button className={`secondary ${styles.toggle}`} onClick={() => setOpen((value) => !value)}>
          {open ? 'Hide scores' : 'Top scores'}
        </button>
      )}
      {open && (
        <div className={styles.board}>
          {loading ? (
            <span className={styles.muted}>Loading scores…</span>
          ) : error ? (
            <span className={styles.warning}>{error}</span>
          ) : entries.length === 0 ? (
            <span className={styles.muted}>No signed-in scores on v{version ?? 1} yet. Be first.</span>
          ) : (
            <>
              <span className={styles.version}>VERSION {version}</span>
              <ol>
                {entries.map((entry) => (
                  <li key={entry.playerId}>
                    <span>
                      <b>{entry.rank}</b>
                      {entry.displayName ?? entry.initials}
                    </span>
                    <strong>{entry.score.toLocaleString()}</strong>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}
